import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { ArrowRight, Loader2, Plus, Tag, Trash2, X } from "lucide-react";
import { Button } from "../ui/button";
import { useWizard } from "react-use-wizard";
import { useMarathonCartStore } from "@/lib/checkoutDetails/checkoutDetails";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  addRegistration,
  checkPaymentStatus,
  fetchSeasonCategory,
} from "@/api/apiFunction";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/components/hooks/use-toast";
import logo from "@/assets/logo.png";
import { Input } from "../ui/input.js";
import axios from "axios";
import { BASE_URL, Image_BASE_URL } from "@/api/apiUrl.js";
import Confetti from "react-dom-confetti";
import { calculateAge } from "@/lib/utils.js";
// @ts-ignore
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { DynamicSelect } from "./component/DynamicSelect.js";
import { useMarathonCartStoreD } from "@/lib/checkoutDetails/memberPrice.js";
import { MemberPrice, sizeOptions } from "./utils/marathon-cart.js";

const MarathonCart = () => {
  const { nextStep } = useWizard();
  const { id } = useParams();
  const { memberPrices, setMemberPrices, removeMemberPrice } =
    useMarathonCartStoreD();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { selectedUsers, removeSelectedUser, updateUser } =
    useMarathonCartStore();
  console.log("selecteList", selectedUsers);
  // Fetching event categories for event
  const { data, isSuccess } = useQuery({
    queryKey: ["season-category"],
    queryFn: () =>
      id ? fetchSeasonCategory(id) : Promise.reject("ID is undefined"),
  });

  // Filter categories based on age
  function filterByAge(data: any[], age: number) {
    // console.log("data", data);
    if (data) {
      const result = data?.filter(
        (item) => age >= item.minAge && age <= item.maxAge
      );

      return result;
    }
  }

  useEffect(() => {
    if (!selectedUsers || !isSuccess || !data) return;
    setMemberPrices((prevPrices) => {
      const newPrices = { ...prevPrices };
      selectedUsers.forEach((member) => {
        // Only set defaults if we have no prior entry for this user
        if (!newPrices[member.id]) {
          const age = calculateAge(member.birthDate);
          const filteredCategories = filterByAge(data, age);

          const defaultCategory = filteredCategories?.[0]?.name || "";
          const defaultCategoryId = filteredCategories?.[0]?.id || null;
          const defaultPrice = filteredCategories?.[0]?.price || 0;

          newPrices[member.id] = {
            id: Number(member.id),
            distance: defaultCategory,
            categoryId: defaultCategoryId,
            price: defaultPrice,
            jerseySize: member.jerseySize || sizeOptions[0]?.name || "",
          };
        }
      });

      return newPrices;
    });
  }, [selectedUsers, isSuccess, data]);

  const handleDistanceSelect = useCallback(
    (memberId: string, selectedDistance: string) => {
      updateUser(memberId, { distance: selectedDistance });
      const selectedCategory = data.find(
        (category: { name: string }) => category.name === selectedDistance
      );
      const selectCategoryId = data.find(
        (category: { name: string; id: string }) =>
          category.name === selectedDistance
      )?.id;

      const price = selectedCategory?.price || 0;

      setMemberPrices((prevPrice: any) => ({
        ...prevPrice,
        [memberId]: {
          ...prevPrice[memberId],
          id: memberId,
          distance: selectedDistance,
          categoryId: selectCategoryId,
          price,
          jerseySize: prevPrice[memberId].jerseySize,
        },
      }));
    },
    [data]
  );

  // Update member's selected T-shirt size
  const handleJerseySize = (memberId: string, selectedSize: string) => {
    setMemberPrices((prevPrice) => ({
      ...prevPrice,
      [memberId]: {
        ...prevPrice[memberId],
        jerseySize: selectedSize, // Save jersey size here
      },
    }));
  };

  const onDeleteHandler = (id: string, isPrimary: boolean) => {
    if (isPrimary) {
      alert("You can't delete primary user");
      return;
    }

    setSelectedUserId(id);
    setIsDialogOpen(true);
  };

  const confirmDeleteHandler = () => {
    if (selectedUserId) {
      removeSelectedUser(selectedUserId);
      removeMemberPrice(selectedUserId);
      setIsDialogOpen(false);
      setSelectedUserId(null);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto bg-white rounded-xl md:shadow-lg overflow-hidden md:mb-56 pb-10">
      <div className="p-6">
        <h1 className="text-2xl text-center font-bold uppercase text-gray-800 mb-6">
          CART
        </h1>
        {selectedUsers === null ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#00c3cc] animate-spin" />
            <Button
              onClick={nextStep}
              className="mt-4 bg-[#00c3cc] hover:bg-[#009fa6] text-white font-bold py-2.5 px-6 rounded-full transition duration-300 ease-in-out transform hover:scale-105 shadow-md"
            >
              ADD MEMBERS
            </Button>
          </div>
        ) : selectedUsers && selectedUsers.length > 0 ? (
          <div className="w-full md:max-w-6xl mx-auto flex justify-center items-center">
            <div className="flex flex-col items-center md:items-start md:flex-row justify-between gap-5 w-full max-w-6xl">
              <section className="w-full md:mb-36 shadow-lg rounded-lg overflow-hidden border-[#7f7f7f]">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="bg-[#595959] text-white font-semibold">
                        Members
                      </TableHead>
                      <TableHead className="bg-[#595959] text-white font-semibold">
                        Categories
                      </TableHead>
                      <TableHead className="bg-[#595959] text-white font-semibold">
                        Size
                      </TableHead>
                      <TableHead className="bg-[#595959] text-white font-semibold">
                        Price
                      </TableHead>
                      <TableHead className="bg-[#595959] text-white font-semibold">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedUsers?.map((member) => {
                      const age = calculateAge(member?.birthDate);
                      let filteredCategories = filterByAge(data, age);
                      return (
                        <TableRow
                          key={member?.id}
                          className="transition-colors hover:bg-gray-50"
                        >
                          <TableCell
                            key={member?.id}
                            className="transition-colors hover:bg-gray-50"
                          >
                            <div className="flex items-center space-x-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage
                                  //@ts-ignore
                                  src={`${Image_BASE_URL}/images/profile/${member?.user?.profileImage}`}
                                />
                                <AvatarFallback className="bg-[#E6F6F7] text-[#00c3cc] font-semibold">
                                  {member?.name[0]?.toLocaleUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {member?.name}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {member?.email}
                                </p>
                                <p className="text-sm text-[#00c3cc]">
                                  Age: {age} years
                                </p>
                                {filteredCategories?.length === 0 && (
                                  <p className="text-xs text-red-500 mt-1">
                                    Not eligible for any category
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <DynamicSelect
                              options={
                                filteredCategories?.map((category) => ({
                                  id: category.id,
                                  name: category.name,
                                })) || []
                              } // Ensure it's an array
                              placeholder="Distance"
                              onSelect={(selectedDistance: string) => {
                                handleDistanceSelect(
                                  member?.id,
                                  selectedDistance
                                );
                              }}
                              //@ts-ignore
                              defaultValue={memberPrices[member?.id]?.distance}
                            />
                          </TableCell>

                          <TableCell>
                            <DynamicSelect
                              options={sizeOptions || []} // Ensure it's an array
                              placeholder="T-Shirt Size"
                              defaultValue={member?.jerseySize}
                              onSelect={(selectedSize: string) =>
                                handleJerseySize(member?.id, selectedSize)
                              }
                            />
                          </TableCell>
                          <TableCell className="text-center text-xs">
                            ₹{memberPrices[member?.id]?.price / 100 || 0}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                //@ts-ignore
                                onDeleteHandler(member.id, member.isPrimary)
                              }
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 transition-opacity duration-200"
                            >
                              <Trash2 className="h-5 w-5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {selectedUsers && selectedUsers.length > 0 && (
                  <div className="my-6 text-center">
                    <Button
                      onClick={nextStep}
                      className="bg-[#00c3cc] hover:bg-[#009fa6] text-white font-bold py-2.5 px-6 rounded-full transition duration-300 ease-in-out transform hover:scale-105 shadow-md"
                    >
                      <Plus className="mr-2 h-4 w-4" /> ADD MEMBERS
                    </Button>
                  </div>
                )}
              </section>
              <section className="w-[350px]">
                <CardTotalComponent
                  selectedMembers={selectedUsers}
                  memberPrices={memberPrices}
                  // jerseySize={jerseySize}
                />
              </section>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No members found.</p>
            <Button
              onClick={nextStep}
              className="bg-[#00c3cc] hover:bg-[#009fa6] text-white font-bold py-2.5 px-6 rounded-full transition duration-300 ease-in-out transform hover:scale-105 shadow-md"
            >
              ADD MEMBERS
            </Button>
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this user?
          </DialogDescription>
          <DialogFooter>
            <Button variant="destructive" onClick={confirmDeleteHandler}>
              Yes, delete
            </Button>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default MarathonCart;

const CardTotalComponent = ({
  selectedMembers,
  memberPrices,
}: {
  selectedMembers: any[];
  memberPrices: { [key: string]: MemberPrice };
}) => {
  const [total, setTotalAmount] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState<number>(0); // Discount in percentage
  const [couponId, setCouponId] = useState();
  const [corporateCouponId, setCorporateCouponId] = useState<number>();
  const [error, setError] = useState("");
  const [couponType, SetCouponType] = useState("");
  const { id } = useParams();
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [discountType, setDiscountType] = useState("");
  const { selectedUsers } = useMarathonCartStore();
  const [loading, setLoading] = useState(true); // Add loading state

  const calculateTotal = () => {
    return selectedMembers.reduce(
      (sum, member) => sum + (memberPrices[member.id]?.price || 0),
      0
    );
  };

  // Check if user is corporate and apply corporate coupon automatically
  useEffect(() => {
    const applyCorporateCoupon = async () => {
      if (
        selectedUsers &&
        selectedUsers[0]?.email &&
        selectedUsers?.length > 0
      ) {
        try {
          const response = await axios.post(
            `${BASE_URL}/couponRedemption/corporate-verification`,
            {
              email: selectedUsers[0]?.email,
              id,
            }
          );
          const data = response.data;

          // Automatically apply corporate coupon
          SetCouponType("CORPORATE");
          setCorporateCouponId(data.corporateId);

          setDiscountType(data.discountType);
          if (data.discountType === "PERCENTAGE") {
            const total = calculateTotal();
            const discountAmount = Math.min(
              (total * data.discountAmount) / 100,
              data.maxDiscountAmount
            );
            setDiscount(discountAmount);
          } else if (data.discountType === "FIXED") {
            setDiscount(data.discountAmount);
          }
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false); // Set loading to false after the request completes
        }
      } else {
        setLoading(false); // Set loading to false if no corporate user is found
      }
    };

    applyCorporateCoupon();
  }, [selectedUsers, couponType]);

  const handleApplyPromoCode = useMutation<
    any,
    unknown,
    { code: string; id: string | undefined }
  >({
    mutationFn: async ({ code, id }) => {
      const response = await axios.post(`${BASE_URL}/couponRedemption/apply`, {
        code,
        id,
      });
      return response.data;
    },
    onError: (error) => {
      setError((error as any)?.response?.data?.message || "Invalid promo code");
      setDiscount(0);
    },
    onSuccess: (data) => {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
      SetCouponType("DISCOUNT");

      setCouponId(data.couponId);

      setDiscountType(data.discountType);
      if (data.discountType === "PERCENTAGE") {
        const total = calculateTotal();

        const discountAmount = Math.min(
          (total * data.discountAmount) / 100 / 100,
          data.maxDiscountAmount
        );

        setDiscount(discountAmount);
      } else if (data.discountType === "FIXED") {
        setDiscount(data.discountAmount);
        setError(data.message);
      }
      setError("");
    },
  });

  const totalBeforeDiscount = Object.values(memberPrices).reduce(
    (total, member) => total + (member.price / 100 || 0),
    0
  );

  const { mutate, data, isPending } = useMutation({
    mutationFn: () => {
      if (!id) {
        throw new Error("ID is undefined");
      }
      //@ts-ignore
      return addRegistration(id, cardTotalData);
    },
    onSuccess: (data) => {
      handlePayment(data.data.orderId);
    },
    onError: (error) => {
      // console.log("Error:", error);
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        //@ts-ignore
        description:
          (error as any)?.response?.data?.message ||
          "There was a problem with your request.",
      });
    },
  });

  const { mutate: paymentMutate } = useMutation({
    mutationFn: () => checkPaymentStatus(data.data.orderId),
    onSuccess: () => {
      toast({
        title: "Payment Successfull!",
        description: "Check My profile to see your past events",
      });
      navigate("/my-registration");
    },
    onError: (error: any) => {
      console.log("Error", error);
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description:
          error?.response?.data?.message ||
          "There was a problem with your request.",
      });
    },
  });

  const handlePayment = async (order_id: string) => {
    var options = {
      key: import.meta.env.VITE_RAZORPAY_KEY_ID,
      currency: "INR",
      name: selectedMembers[0].name, //your business name
      description: "Test Transaction",
      image: logo,
      order_id: order_id,
      handler: function () {
        paymentMutate();
      },
      prefill: {
        name: selectedMembers[0].name, //your customer's name
        email: selectedMembers[0].email,
        contact: selectedMembers[0].mobile, //Provide the customer's phone number for better conversion rates
      },
      notes: {
        address: "Razorpay Corporate Office",
      },
      theme: {
        color: "#00A8A9CC",
      },
    };

    const Razorpay = new (window as any).Razorpay(options);
    Razorpay.open();
    Razorpay.on("payment.failed", function (response: any) {
      alert(response.error.reason);
      toast.error("Payment failed");
    });
  };

  useEffect(() => {
    const total = selectedMembers.reduce((sum, member) => {
      return sum + (memberPrices[member?.id]?.price || 0);
    }, 0);
    setTotalAmount(Number((total / 100).toFixed(2)));
  }, [selectedMembers, memberPrices]);

  selectedUsers.map((user) => user?.jerseySize);
  const members = Object.values(memberPrices).map((member) => {
    const user = selectedUsers.find(
      (user) => user?.id.toString() === member?.id.toString()
    );
    return {
      id: member?.id,
      categoryId: member?.categoryId,
      categoryName: member?.distance,
      jerseySize: member?.jerseySize || user?.jerseySize,
      price: member?.price,
    };
  });

  const cardTotalData = {
    totalAmount: (totalBeforeDiscount - discount) * 100,
    members: members,
    couponId: couponId,
    corporateCouponId: couponId === undefined ? corporateCouponId : undefined,
    couponType: couponType,
  };

  const handlePromoCodeApply = (id: string | undefined) => {
    handleApplyPromoCode.mutate({ code: promoCode, id });
  };

  return (
    <Card className="w-[22rem] mb-32 md:mb-0  drop-shadow-lg  border rounded-xl shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-[#00c3cc] to-[#009fa6] py-4">
        <CardTitle className="text-center text-white text-lg font-bold">
          CART SUMMARY
        </CardTitle>
      </div>

      <CardContent className="p-6">
        <div className="space-y-4">
          {selectedMembers.map((member) => (
            <div
              key={member?.id}
              className="flex justify-between items-center py-2 border-b border-gray-100"
            >
              <p className="text-gray-700 font-medium">{member?.name}</p>
              <p className="text-[#595959] font-bold">
                ₹{" "}
                {memberPrices[member?.id]?.price
                  ? (memberPrices[member?.id]?.price / 100).toFixed(2)
                  : "N/A"}
              </p>
            </div>
          ))}

          <div>
            {/* Promo Code Input */}
            <div className="relative mt-6">
              <Input
                placeholder="Enter promo code"
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                className="w-full pr-24 border-2 border-gray-200 focus:border-[#00c3cc] rounded-full"
              />

              <Button
                type="button"
                onClick={() => handlePromoCodeApply(id)}
                className="absolute right-0 top-0 h-full px-4 bg-[#00c3cc] text-white rounded-r-full hover:bg-[#009fa6] transition-colors duration-300"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Apply"}
              </Button>
            </div>
          </div>
          {couponId !== "" && !error && discountType && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
              <Tag className="text-green-500 mr-2" size={18} />
              <p className="text-green-700 text-sm">
                {discountType === "FIXED"
                  ? `Flat ₹${discount} discount applied successfully!`
                  : `Flat ₹${discount} discount applied successfully!`}
              </p>
              <button
                onClick={() => {
                  setPromoCode("");
                  setDiscountType("");
                  setDiscount(0);
                }}
                className="ml-auto text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
          )}
          {/* Show Error if promo code is invalid */}
          {error && <p className="text-red-500 text-sm mt-1">{error}</p>}

          {/* Total before and after discount */}
          <div className="space-y-2 pt-4">
            <div className="flex justify-between text-gray-600">
              <p>Subtotal:</p>
              {/* <p>₹ {totalBeforeDiscount.toFixed(2)}</p> */}
              <p>₹ {total.toFixed(2)}</p>
            </div>

            {discount > 0 && (
              <div className="flex justify-between text-gray-600">
                <p>Discount:</p>
                <p>- ₹ {discount.toFixed(2)}</p>
              </div>
            )}
          </div>
          <div className="border-t border-gray-200 mt-4 pt-4">
            <div className="flex justify-between items-center">
              <p className="text-[#333333] text-lg font-bold">Order Total:</p>
              <p className="text-[#333333] text-2xl font-bold">
                ₹{" "}
                {/* {(
                  totalBeforeDiscount - Number.parseFloat(discount.toFixed(2))
                ).toFixed(2)} */}
                {(total - discount).toFixed(2)}
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              // console.log("cardTotalData", cardTotalData);
              mutate();
            }}
            disabled={isPending || selectedMembers.length === 0}
            className={`w-full bg-[#00c3cc] hover:bg-[#009fa6] text-white font-bold py-2.5 px-6 rounded-full transition duration-300 ease-in-out transform hover:scale-105 shadow-md" ${
              selectedMembers.length === 0 ? "cursor-not-allowed" : ""
            }`}
          >
            PROCEED TO PAYMENT <ArrowRight />
          </Button>
        </div>
      </CardContent>
      <Confetti
        active={showConfetti}
        config={{ elementCount: 100, spread: 60 }}
      />
    </Card>
  );
};

